import { Pipe, PipeTransform } from "@angular/core";
import { TopicTagInterface } from "@core/interfaces/forums/topic.interface";
import { UtilsService } from "@core/services/utils/utils.service";

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  s /= 100;
  l /= 100;

  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

  return [Math.round(255 * f(0)), Math.round(255 * f(8)), Math.round(255 * f(4))];
}

/**
 * Generates text colors for topic tags with optimal contrast ratio for readability
 *
 * This pipe analyzes the background color's lightness and determines
 * whether to use a dark or light text color to ensure WCAG compliance.
 * The goal is to achieve at least a 4.5:1 contrast ratio for readability.
 */
@Pipe({ name: "tagTextColor", pure: true })
export class TagTextColorPipe implements PipeTransform {
  transform(tag: TopicTagInterface): string {
    if (!tag || !tag.category) {
      return "rgba(40, 40, 45, 0.85)"; // Dark text for the default blue background
    }

    const hashString = tag.subcategory ? `${tag.category}-${tag.subcategory}` : tag.category;
    const hash = UtilsService.stringToHash(hashString.toLowerCase());

    const hue = hash % 360;
    const saturation = 45 + (hash % 20); // Match background saturation
    const lightness = 55 + (hash % 15); // Match background lightness

    const bgRgb = hslToRgb(hue, saturation, lightness);

    // Calculate brightness using a perceptual formula
    const brightness = (bgRgb[0] * 0.299 + bgRgb[1] * 0.587 + bgRgb[2] * 0.114) / 255;

    // Use a moderate threshold for readability
    if (brightness > 0.85) {
      // For lighter backgrounds
      return "rgba(40, 40, 45, 0.85)"; // Dark gray with a hint of blue, but not too dark
    } else {
      // For darker backgrounds
      return "rgba(170, 170, 175, 0.9)"; // Off-white that's not too bright
    }
  }
}
